import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Cropper from "react-easy-crop";
import axios from "axios";
import { toast } from "sonner";
import { UserRound, Loader2, Save, ShieldCheck, Mail, KeyRound, LogOut, ImagePlus, Activity, X, ZoomIn } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";
import { ATHLETE_TOKEN_KEY as TOKEN_KEY, notifyAthleteAuthChanged, signOutAthlete } from "@/components/AccountMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-4 py-3 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

const EMPTY = {
  name: "", email: "", phone: "", city: "", address: "", pincode: "",
  blood_group: "", gender: "", dob: "", tshirt_size: "",
  emergency_name: "", emergency_phone: "", emergency_relationship: "",
};

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const formatDistance = (meters) => `${((meters || 0) / 1000).toFixed(1)} km`;
const formatDuration = (seconds) => {
  const total = Number(seconds || 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  return hours ? `${hours}h ${minutes}m` : `${minutes} min`;
};

const cropImageToBlob = (imageSrc, cropPixels, contentType) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(cropPixels.width));
      canvas.height = Math.max(1, Math.round(cropPixels.height));
      const context = canvas.getContext("2d");
      context.drawImage(
        image,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not crop image"))), contentType, 0.92);
    };
    image.onerror = () => reject(new Error("Could not read image"));
    image.src = imageSrc;
  });

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] mb-2">{label}</label>
      {children}
    </div>
  );
}

function LoginCard({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/request-code`, { email });
      setSent(true);
      toast.success("If that email is registered, a 6-digit code is on its way.");
    } catch {
      toast.error("Could not send the code — please try again");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (busy || code.length !== 6) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/verify-code`, { email, code });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      notifyAthleteAuthChanged();
      toast.success(`Welcome back${r.data.name ? `, ${r.data.name.split(" ")[0]}` : ""}.`);
      onLoggedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="max-w-md mx-auto rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-8 sm:p-10"
      data-testid="athlete-login-card"
    >
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
        <UserRound className="w-4 h-4" /> Athlete Sign-In
      </p>
      <h1 className="font-serif text-3xl sm:text-4xl font-light text-[#0D1117] tracking-tight mb-4">
        Your race <span className="italic text-[#FF4E00]">passport.</span>
      </h1>
      <p className="text-sm text-[#374151] leading-relaxed mb-8">
        Sign in with the email you registered with — we'll send a one-time code. No passwords, ever.
      </p>
      {!sent ? (
        <form onSubmit={requestCode} className="space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className={inputCls}
            data-testid="login-email"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="login-send-code"
            className="w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Email Me a Code
          </button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151]">
            Code sent to {email} — valid 10 minutes
          </p>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="••••••"
            className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`}
            data-testid="login-code"
          />
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            data-testid="login-verify"
            className="w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Sign In
          </button>
          <button type="button" onClick={() => { setSent(false); setCode(""); }} className="w-full font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] hover:text-[#FF4E00] transition-colors" data-testid="login-change-email">
            Use a different email
          </button>
        </form>
      )}
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mt-6 flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-[#FF4E00]" /> New here? <Link to="/signup" data-testid="login-signup-link" className="text-[#FF4E00] hover:text-[#E04000]">Create your account</Link>
      </p>
    </motion.div>
  );
}

export default function Profile() {
  const [form, setForm] = useState(EMPTY);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [cropSource, setCropSource] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [cropBusy, setCropBusy] = useState(false);
  const [history, setHistory] = useState({ connected: false, summary: { activities: 0, distance_m: 0, moving_time_s: 0 }, activities: [] });

  useEffect(() => {
    if (!token) {
      setLoaded(false);
      return undefined;
    }
    let active = true;
    let photoUrl = "";
    const load = async () => {
      try {
        const response = await axios.get(`${API}/athlete/profile`, { headers: { Authorization: `Bearer ${token}` } });
        if (!active) return;
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(response.data).filter(([key]) => key in EMPTY)) });
        try {
          const historyResponse = await axios.get(`${API}/athlete/running-history`, { headers: { Authorization: `Bearer ${token}` } });
          if (active) setHistory(historyResponse.data);
        } catch {
          if (active) setHistory({ connected: false, summary: { activities: 0, distance_m: 0, moving_time_s: 0 }, activities: [] });
        }
        if (response.data.photo_path) {
          try {
            const photo = await axios.get(`${API}/athlete/profile/photo`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
            photoUrl = URL.createObjectURL(photo.data);
            if (active) setPhotoPreview(photoUrl);
          } catch {
            if (active) setPhotoPreview("");
          }
        }
        if (active) setLoaded(true);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        notifyAthleteAuthChanged();
        setToken("");
        setAuthFailed(true);
      }
    };
    load();
    return () => {
      active = false;
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const signOut = () => {
    signOutAthlete();
    if (photoPreview.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    setToken("");
    setForm(EMPTY);
    setPhotoFile(null);
    setPhotoPreview("");
    if (cropSource) URL.revokeObjectURL(cropSource);
    setCropSource("");
    setCropFile(null);
    setCropPixels(null);
    setHistory({ connected: false, summary: { activities: 0, distance_m: 0, moving_time_s: 0 }, activities: [] });
    setLoaded(false);
    setAuthFailed(false);
  };

  const choosePhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Choose a JPG, PNG or WebP image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Profile photo must be under 5 MB");
      return;
    }
    if (cropSource) URL.revokeObjectURL(cropSource);
    setCropFile(file);
    setCropSource(URL.createObjectURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropPixels(null);
    setPhotoFile(null);
    event.target.value = "";
  };

  const cancelCrop = () => {
    if (cropSource) URL.revokeObjectURL(cropSource);
    setCropSource("");
    setCropFile(null);
    setCropPixels(null);
    setCropBusy(false);
  };

  const applyCrop = async () => {
    if (!cropFile || !cropPixels || cropBusy) return;
    setCropBusy(true);
    try {
      const blob = await cropImageToBlob(cropSource, cropPixels, cropFile.type);
      const extension = cropFile.type === "image/png" ? "png" : cropFile.type === "image/webp" ? "webp" : "jpg";
      const croppedFile = new File([blob], `profile-photo.${extension}`, { type: cropFile.type });
      if (photoPreview.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
      setPhotoFile(croppedFile);
      setPhotoPreview(URL.createObjectURL(croppedFile));
      cancelCrop();
      toast.success("Crop ready — upload it to save.");
    } catch {
      toast.error("Could not crop this photo");
      setCropBusy(false);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile || photoBusy || !token) return;
    setPhotoBusy(true);
    try {
      const data = new FormData();
      data.append("photo", photoFile);
      await axios.post(`${API}/athlete/profile/photo`, data, { headers: { Authorization: `Bearer ${token}` } });
      setPhotoFile(null);
      toast.success("Profile photo updated.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not upload your photo");
    } finally {
      setPhotoBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !token) return;
    setBusy(true);
    try {
      const r = await axios.put(`${API}/athlete/profile`, form, { headers: { Authorization: `Bearer ${token}` } });
      setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(r.data).filter(([k]) => k in EMPTY)) });
      setLoaded(true);
      toast.success("Profile updated — you're race-kit ready.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your profile");
    } finally {
      setBusy(false);
    }
  };

  const authed = loaded && token;
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FBF9F5]">
        <PageHeader />
        <main className="px-6 sm:px-12 pt-32 sm:pt-40 pb-24" data-testid="profile-login-gate">
          <LoginCard onLoggedIn={() => { setToken(localStorage.getItem(TOKEN_KEY) || ""); setAuthFailed(false); }} />
          {authFailed && (
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700 mt-6">
              Your session expired — please sign in again
            </p>
          )}
        </main>
        <Footer />
      </div>
    );
  }

  return (    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-28 sm:pt-36 pb-24 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <UserRound className="w-4 h-4" /> My Profile
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-[#0D1117] tracking-tight mb-5">
            Your race <span className="italic text-[#FF4E00]">passport.</span>
          </h1>
          <p className="text-base text-[#374151] max-w-xl leading-relaxed">
            Your verified race passport is ready. Add a profile photo, connect Strava for running history,
            and keep race-day details current — every challenge you join picks this up automatically.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] mt-4 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[#FF4E00]" /> Private — visible only to you and race control
          </p>
          {token && (
            <button onClick={signOut} data-testid="profile-sign-out" className="mt-5 inline-flex items-center gap-2 border border-[#0D1117]/15 hover:border-[#FF4E00] hover:text-[#FF4E00] text-[#374151] font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-2.5 rounded-full transition-all duration-300">
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-testid="profile-setup-dashboard">
          <section className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8" data-testid="profile-photo-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5 flex items-center gap-2">
              <ImagePlus className="w-3.5 h-3.5" /> Profile Photo
            </p>
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-full border border-[#0D1117]/10 bg-[#FBF9F5] overflow-hidden flex items-center justify-center" data-testid="profile-photo-preview">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile preview" className="w-full h-full object-cover" />
                ) : (
                  <UserRound className="w-10 h-10 text-[#6B7280]" />
                )}
              </div>
              <div className="flex-1 space-y-3">
                <label className="inline-flex cursor-pointer border border-[#0D1117]/15 hover:border-[#FF4E00] hover:text-[#FF4E00] text-[#374151] font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-all duration-300" data-testid="profile-photo-choose-label">
                  Choose Photo
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={choosePhoto} className="hidden" data-testid="profile-photo-input" />
                </label>
                <button type="button" onClick={uploadPhoto} disabled={!photoFile || photoBusy} data-testid="profile-photo-upload" className="w-full inline-flex items-center justify-center gap-2 bg-[#0D1117] hover:bg-[#FF4E00] disabled:opacity-50 text-[#FBF9F5] font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-all duration-300">
                  {photoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                  Upload Cropped Photo
                </button>
                <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#6B7280]">Crop and zoom before upload · JPG, PNG or WebP · max 5 MB</p>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8" data-testid="running-history-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" /> Running History
            </p>
            {history.connected || history.activities.length > 0 ? (
              <div>
                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div><p className="font-serif text-2xl">{history.summary.activities}</p><p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280]">Activities</p></div>
                  <div><p className="font-serif text-2xl">{formatDistance(history.summary.distance_m)}</p><p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280]">Distance</p></div>
                  <div><p className="font-serif text-2xl">{formatDuration(history.summary.moving_time_s)}</p><p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280]">Moving Time</p></div>
                </div>
                <div className="space-y-3" data-testid="running-history-list">
                  {history.activities.slice(0, 3).map((activity) => (
                    <div key={activity.provider_activity_id || activity.strava_activity_id} className="flex items-center justify-between gap-4 rounded-2xl bg-[#FBF9F5] px-4 py-3">
                      <div className="min-w-0"><p className="text-sm font-medium truncate">{activity.name}</p><p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#6B7280]">{activity.start_date?.slice(0, 10)} · {activity.challenge_slug}</p></div>
                      <p className="font-mono text-xs text-[#FF4E00] whitespace-nowrap">{formatDistance(activity.distance)}</p>
                    </div>
                  ))}
                </div>
                <Link to="/challenges" data-testid="running-history-sync-link" className="inline-flex mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#FF4E00] hover:text-[#E04000]">Sync more from a challenge</Link>
              </div>
            ) : (
              <div>
                <p className="text-sm text-[#374151] leading-relaxed mb-5">Connect Strava from any challenge to pull your verified running history here. No manual entry needed.</p>
                <Link to="/challenges" data-testid="connect-strava-link" className="inline-flex items-center gap-2 bg-[#FC4C02] hover:bg-[#E34402] text-[#FFFFFF] font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-colors duration-300">
                  <Activity className="w-3.5 h-3.5" /> Connect Strava
                </Link>
              </div>
            )}
          </section>
        </div>

        <form onSubmit={submit} className="space-y-8" data-testid="profile-form">
          <section className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8 space-y-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Identity</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name *"><input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="profile-name" /></Field>
              <Field label="Email *"><input required type="email" value={form.email} readOnly aria-readonly="true" placeholder="you@email.com" className={`${inputCls} opacity-70 cursor-not-allowed`} data-testid="profile-email" /></Field>
              <Field label="Phone *"><input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98100 12345" className={inputCls} data-testid="profile-phone" /></Field>
              <Field label="City *"><input required value={form.city} onChange={set("city")} placeholder="New Delhi" className={inputCls} data-testid="profile-city" /></Field>
              <Field label="Date of birth *"><input required type="date" value={form.dob} onChange={set("dob")} max={new Date().toISOString().slice(0, 10)} className={inputCls} data-testid="profile-dob" /></Field>
              <Field label="Gender *">
                <select required value={form.gender} onChange={set("gender")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="profile-gender">
                  <option value="" className="bg-[#FFFFFF]">Select</option>
                  {GENDERS.map((g) => <option key={g} value={g} className="bg-[#FFFFFF]">{g}</option>)}
                </select>
              </Field>
              <Field label="Blood group *">
                <select required value={form.blood_group} onChange={set("blood_group")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="profile-blood">
                  <option value="" className="bg-[#FFFFFF]">Select</option>
                  {BLOOD_GROUPS.map((b) => <option key={b} value={b} className="bg-[#FFFFFF]">{b}</option>)}
                </select>
              </Field>
              <Field label="T-shirt size *">
                <select required value={form.tshirt_size} onChange={set("tshirt_size")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="profile-tshirt">
                  <option value="" className="bg-[#FFFFFF]">Select</option>
                  {SIZES.map((s) => <option key={s} value={s} className="bg-[#FFFFFF]">{s}</option>)}
                </select>
              </Field>
            </div>
          </section>

          <section className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8 space-y-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Address</p>
            <Field label="Address">
              <textarea rows={2} value={form.address} onChange={set("address")} placeholder="Flat, street, city, state" className={`${inputCls} resize-none`} data-testid="profile-address" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Pincode"><input value={form.pincode} onChange={set("pincode")} placeholder="110001" className={inputCls} data-testid="profile-pincode" /></Field>
            </div>
          </section>

          <section className="rounded-3xl border border-[#FF4E00]/25 bg-[#FFFFFF] p-6 sm:p-8 space-y-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Emergency Contact</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Field label="Name *"><input required value={form.emergency_name} onChange={set("emergency_name")} placeholder="Contact person" className={inputCls} data-testid="profile-emergency-name" /></Field>
              <Field label="Phone *"><input required type="tel" value={form.emergency_phone} onChange={set("emergency_phone")} placeholder="+91 ..." className={inputCls} data-testid="profile-emergency-phone" /></Field>
              <Field label="Relationship *"><input required value={form.emergency_relationship} onChange={set("emergency_relationship")} placeholder="e.g. Spouse" className={inputCls} data-testid="profile-emergency-relationship" /></Field>
            </div>
          </section>

          <button
            type="submit"
            disabled={busy}
            data-testid="profile-save"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 rounded-full transition-all duration-300"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {loaded ? "Update Profile" : "Save Profile"}
          </button>
        </form>

        {cropSource && (
          <div className="fixed inset-0 z-[80] bg-[#0D1117]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="photo-crop-modal">
            <div className="w-full max-w-2xl rounded-3xl bg-[#FFFFFF] p-5 sm:p-7 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Frame Your Photo</p>
                  <h2 className="font-serif text-2xl font-light text-[#0D1117] mt-1">Drag to position, zoom to fit.</h2>
                </div>
                <button type="button" onClick={cancelCrop} data-testid="photo-crop-cancel-icon" aria-label="Cancel crop" className="w-9 h-9 rounded-full border border-[#0D1117]/10 flex items-center justify-center text-[#374151] hover:text-[#FF4E00] hover:border-[#FF4E00] transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="relative h-[320px] sm:h-[420px] rounded-2xl overflow-hidden bg-[#0D1117]" data-testid="photo-crop-area">
                <Cropper
                  image={cropSource}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, pixels) => setCropPixels(pixels)}
                />
              </div>
              <div className="mt-5 flex items-center gap-4">
                <ZoomIn className="w-4 h-4 text-[#FF4E00]" />
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                  className="flex-1 accent-[#FF4E00]"
                  data-testid="photo-crop-zoom"
                  aria-label="Zoom photo"
                />
                <span className="font-mono text-[10px] text-[#6B7280] w-12 text-right" data-testid="photo-crop-zoom-value">{zoom.toFixed(2)}×</span>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button type="button" onClick={cancelCrop} data-testid="photo-crop-cancel" className="border border-[#0D1117]/15 hover:border-[#FF4E00] hover:text-[#FF4E00] text-[#374151] font-mono text-[10px] uppercase tracking-[0.18em] px-6 py-3 rounded-full transition-all duration-300">
                  Cancel
                </button>
                <button type="button" onClick={applyCrop} disabled={!cropPixels || cropBusy} data-testid="photo-crop-apply" className="inline-flex items-center justify-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-[10px] uppercase tracking-[0.18em] px-6 py-3 rounded-full transition-all duration-300">
                  {cropBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                  Use This Crop
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
